import actionTypes from 'constants/actionTypes'
import direction from 'constants/direction'
import defaultsDeep from 'lodash/defaultsDeep'
import { isHardFail } from 'lib/action'

interface EdgeDataState {
  content: string
  kind: string
  directionality: string
  from: string
  to: string
  readers: unknown[]
  writers: unknown[]
  creator: unknown
}

interface EdgeState {
  data: EdgeDataState
  syncData: EdgeDataState | null
  local: boolean
  isFetching: boolean
  deleted: boolean
  failed: boolean
  id: string
  modified: number | null
  modifed?: number
}

interface EdgeInput {
  id?: string
  from?: string
  to?: string
  content?: string
  kind?: string
  directionality?: string
  readers?: unknown[]
  writers?: unknown[]
  creator?: unknown
  modified?: string | number
  [key: string]: unknown
}

interface EdgeAction {
  type?: string
  payload?: {
    from?: string
    to?: string
    content?: string
    kind?: string
    directionality?: string
    readers?: unknown[]
    writers?: unknown[]
    creator?: unknown
    data?: EdgeInput
  }
  meta?: {
    isRetry?: boolean
    previousAction?: {
      meta?: {
        receivedAt?: number
      }
    }
  }
}

const initialState: EdgeState = {
  data: {
    content: '',
    kind: '',
    directionality: direction.DIRECTED,
    from: '',
    to: '',
    readers: [],
    writers: [],
    creator: null
  },
  syncData: null,
  local: false,
  isFetching: false,
  deleted: false,
  failed: false,
  id: '',
  modified: null,
}


const single = (state: EdgeState = initialState, action: EdgeAction, edge: EdgeInput = {}): EdgeState => {
  state.id = edge.id ?? state.id
  switch (action.type) {
    case actionTypes.GRAPH.GET_NEIGHBORHOOD.RECEIVE: {
      if (state.isFetching) return state
      if (state.modified && (action.meta?.previousAction?.meta?.receivedAt ?? 0) < state.modified) return state
      const data = defaultsDeep(
        {
          from: edge.from,
          to: edge.to,
          content: edge.content,
          kind: edge.kind,
          directionality: edge.directionality,
          readers: edge.readers,
          writers: edge.writers,
          creator: edge.creator
        },
        state.data
      )
      return defaultsDeep(
        {
          syncData: data,
          data: data,
          isFetching: false,
          deleted: false,
          local: false,
          modified: new Date(edge.modified ?? Date.now()).getTime() || Date.now()
        },
        state
      )
    }
    case actionTypes.EDGES.ONE.CREATE.REQUEST: {
      let data: Partial<EdgeDataState> = {}
      if (typeof action.payload?.from === 'string') data.from = action.payload.from
      if (typeof action.payload?.to === 'string') data.to = action.payload.to
      if (typeof action.payload?.content === 'string') data.content = action.payload.content
      if (typeof action.payload?.kind === 'string') data.kind = action.payload.kind
      if (typeof action.payload?.directionality === 'string') data.directionality = action.payload.directionality
      if (Array.isArray(action.payload?.readers)) data.readers = action.payload.readers
      if (Array.isArray(action.payload?.writers)) data.writers = action.payload.writers
      if (action.payload?.creator !== undefined) data.creator = action.payload.creator
      if (action.meta?.isRetry) data = {}
      return defaultsDeep(
        {
          data: data,
          local: true,
          isFetching: true,
          deleted: false,
          modified: Date.now()
        },
        state
      )
    }
    case actionTypes.EDGES.ONE.CREATE.RECEIVE:
    case actionTypes.EDGES.ONE.CREATE.NOTIFY: {
      const payloadData = action.payload?.data
      const data = defaultsDeep(
        {
          from: payloadData?.from,
          to: payloadData?.to,
          content: payloadData?.content,
          kind: payloadData?.kind,
          directionality: payloadData?.directionality,
          readers: payloadData?.readers,
          writers: payloadData?.writers,
          creator: payloadData?.creator
        },
        state.data
      )
      return defaultsDeep(
        {
          syncData: data,
          data: data,
          local: false,
          isFetching: false,
          deleted: false,
          failed: false,
          modified: new Date(payloadData?.modified ?? Date.now()).getTime() || Date.now()
        },
        state
      )
    }
    case actionTypes.EDGES.ONE.CREATE.FAIL: {
      const failedState = defaultsDeep(
        {
          isFetching: false,
          failed: true
        },
        state
      )
      if (isHardFail(action)) {
        failedState.data = state.syncData
        failedState.failed = false
      }
      return failedState
    }
    case actionTypes.EDGES.ONE.EDIT.REQUEST: {
      let data: Partial<EdgeDataState> = {}
      if (typeof action.payload?.content === 'string') data.content = action.payload.content
      if (typeof action.payload?.kind === 'string') data.kind = action.payload.kind
      if (typeof action.payload?.directionality === 'string') {
        if (action.payload.directionality === direction.REVERSE) {
          data.from = state.data.to
          data.to = state.data.from
          data.directionality = direction.DIRECTED
        } else {
          data.directionality = action.payload.directionality
        }
      }
      if (action.meta?.isRetry) data = {}
      return defaultsDeep(
        {
          data: data,
          isFetching: true
        },
        state
      )
    }
    case actionTypes.EDGES.ONE.EDIT.RECEIVE:
    case actionTypes.EDGES.ONE.EDIT.NOTIFY: {
      const payloadData = action.payload?.data
      const data = defaultsDeep(
        {
          content: payloadData?.content,
          kind: payloadData?.kind,
          directionality: payloadData?.directionality
        },
        state.data
      )
      return defaultsDeep(
        {
          syncData: data,
          data: data,
          isFetching: false,
          failed: false,
          modified: new Date(payloadData?.modified ?? Date.now()).getTime() || Date.now()
        },
        state
      )
    }
    case actionTypes.EDGES.ONE.EDIT.FAIL: {
      const failedState = defaultsDeep(
        {
          isFetching: false,
          failed: true
        },
        state
      )
      if (isHardFail(action)) {
        failedState.data = state.syncData
        failedState.failed = false
      }
      return failedState
    }
    case actionTypes.EDGES.ONE.DELETE.REQUEST:
      return defaultsDeep(
        {
          isFetching: true,
          deleted: true
        },
        state
      )
    case actionTypes.EDGES.ONE.DELETE.RECEIVE:
    case actionTypes.EDGES.ONE.DELETE.NOTIFY: {
      const update: Partial<EdgeState> = { isFetching: false }
      if (!state.local) update.deleted = true
      update.modified = Date.now()
      if (action.payload?.data?.modified) update.modified = new Date(action.payload.data.modified).getTime()
      return defaultsDeep(update, state)
    }
    case actionTypes.EDGES.ONE.DELETE.FAIL: {
      const failedState = defaultsDeep(
        {
          isFetching: false,
          failed: true
        },
        state
      )
      if (isHardFail(action)) {
        failedState.deleted = false
        failedState.failed = false
      }
      return failedState
    }
    case actionTypes.EDGES.MULTI.DELETE.REQUEST:
      return defaultsDeep(
        {
          isFetching: true,
          deleted: true
        },
        state
      )
    case actionTypes.EDGES.MULTI.DELETE.RECEIVE:
    case actionTypes.EDGES.MULTI.DELETE.NOTIFY: {
      const update: Partial<EdgeState> = { isFetching: false }
      if (!state.local) update.deleted = true
      update.modifed = Date.now()
      return defaultsDeep(update, state)
    }
    case actionTypes.EDGES.MULTI.DELETE.FAIL: {
      const failedState = defaultsDeep(
        {
          isFetching: false,
          failed: true
        },
        state
      )
      if (isHardFail(action)) {
        failedState.deleted = false
        failedState.failed = false
      }
      return failedState
    }
    default:
      return state
  }
}

export default single
